import { IonIcon } from '@ionic/react'
import { msg } from '@lingui/core/macro'
import { useLingui } from '@lingui/react'
import { alertCircle, checkmarkCircle, refresh } from 'ionicons/icons'
import { DateTime } from 'luxon'
import { useEffect, useState } from 'react'
import { useMutation, useQuery } from 'urql'
import {
  GetPreWorkAuthorization,
  TriggerPreWorkAuthorization,
} from '../../graphql/preWorkAuthorization'
import { useNetwork } from '../../hooks/useNetwork'
import { useOrganization } from '../../hooks/useOrganization'
import { useToast } from '../../hooks/useToast'

const ORDER_ON_SITE = 'order.on_site'

export type PreWorkAuthorizationSectionProps = {
  orderId: number
}

export const PreWorkAuthorizationSection = ({
  orderId,
}: PreWorkAuthorizationSectionProps) => {
  const { _ } = useLingui()
  const { isOnline } = useNetwork()
  const [presentToast] = useToast()
  const { organization } = useOrganization()

  const enabled = organization?.requestPreWorkAuthorization === true

  const [{ data, fetching }, reexecute] = useQuery({
    query: GetPreWorkAuthorization,
    variables: { orderId },
    requestPolicy: 'cache-and-network',
    pause: !enabled,
  })

  const [{ fetching: requesting }, requestAuthorization] = useMutation(
    TriggerPreWorkAuthorization,
  )

  const [checkedAt, setCheckedAt] = useState<DateTime | null>(null)

  useEffect(() => {
    if (fetching || !data) return
    setCheckedAt(DateTime.now())
  }, [fetching, data])

  const authorization = data?.order?.signatures.find(
    (entry) => entry.purpose === 'pre_work_authorization',
  )
  const authorized = authorization?.signed === true

  const handleRefresh = () => {
    reexecute({ requestPolicy: 'network-only' })
  }

  const handleRequest = async () => {
    const result = await requestAuthorization({
      input: { type: ORDER_ON_SITE, orderId },
    })

    if (result.error) {
      presentToast({
        message: _(msg`An error occurred, please try again.`),
        duration: 3000,
      })
      return
    }

    presentToast({
      message: _(msg`Authorization request sent.`),
      duration: 3000,
    })
    reexecute({ requestPolicy: 'network-only' })
  }

  if (!enabled) return null

  return (
    <div
      className={`prework-auth ${authorized ? 'prework-auth--authorized' : ''}`}
    >
      <div className='prework-auth__header'>
        <IonIcon
          className='prework-auth__icon'
          icon={authorized ? checkmarkCircle : alertCircle}
        />
        {/* Live region limited to the status text, not the controls */}
        <output className='prework-auth__text' aria-live='polite'>
          {authorized
            ? _(msg`Work authorized by the customer.`)
            : _(msg`Work not authorized by the customer yet.`)}
        </output>
        {!authorized && (
          <button
            type='button'
            className='prework-auth__refresh'
            onClick={handleRefresh}
            disabled={!isOnline || fetching}
            aria-label={_(msg`Refresh authorization status`)}
          >
            <IonIcon icon={refresh} />
          </button>
        )}
      </div>

      <span className='prework-auth__timestamp'>
        {checkedAt
          ? _(msg`Updated ${checkedAt.toFormat('t')}`)
          : _(msg`Checking…`)}
      </span>

      {!authorized && (
        <button
          type='button'
          className='prework-auth__request'
          onClick={handleRequest}
          disabled={!isOnline || requesting}
        >
          {_(msg`Request authorization`)}
        </button>
      )}
    </div>
  )
}
